import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Check, X, ScanFace, MapPin, ShieldQuestion, CheckCircle2 } from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { toast } from "sonner";

function Photo({ path, label }) {
  const [ok, setOk] = useState(true);
  return (
    <div className="flex-1">
      <p className="text-[11px] font-semibold uppercase text-slate-400 mb-1.5 text-center">{label}</p>
      <div className="aspect-square rounded-xl overflow-hidden border border-slate-200 bg-slate-50 grid place-items-center">
        {path && ok ? (
          <img src={fileUrl(path)} alt={label} onError={() => setOk(false)} className="h-full w-full object-cover" />
        ) : (
          <div className="text-slate-300 flex flex-col items-center gap-1"><ScanFace className="h-8 w-8" /><span className="text-xs">No photo</span></div>
        )}
      </div>
    </div>
  );
}

function fmt(ts) {
  if (!ts) return "—";
  return new Date(ts).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function Reviews() {
  const qc = useQueryClient();
  const [busyId, setBusyId] = useState(null);
  const { data: reviews = [], isLoading } = useQuery({ queryKey: ["reviews"], queryFn: () => api.get("/admin/reviews") });
  const { data: employees = [] } = useQuery({ queryKey: ["employees"], queryFn: () => api.get("/admin/employees") });

  const masterOf = (userId) => employees.find((e) => e.id === userId)?.master_photo_path;

  const decide = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/admin/attendance/${id}/review`, { status }),
    onMutate: (v) => setBusyId(v.id),
    onSuccess: (_, v) => {
      toast.success(v.status === "present" ? "Attendance approved" : "Attendance rejected");
      qc.invalidateQueries({ queryKey: ["reviews"] });
      qc.invalidateQueries({ queryKey: ["attendance"] });
    },
    onError: (e) => toast.error(apiError(e)),
    onSettled: () => setBusyId(null),
  });

  return (
    <div className="space-y-5 animate-fade-up" data-testid="reviews-page">
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200" data-testid="reviews-count">
          <ShieldQuestion className="h-4 w-4" /> {reviews.length} pending review(s)
        </span>
      </div>

      {isLoading ? (
        <div className="py-16 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>
      ) : reviews.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm py-20 flex flex-col items-center gap-3">
          <div className="grid place-items-center h-16 w-16 rounded-full bg-emerald-50 text-emerald-600"><CheckCircle2 className="h-8 w-8" /></div>
          <p className="font-head text-lg font-semibold text-slate-900">All caught up</p>
          <p className="text-sm text-slate-400">No clock-ins are waiting for manual review.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="reviews-grid">
          {reviews.map((r) => {
            const conf = r.audit_result?.confidence;
            return (
              <div key={r.id} className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid={`review-card-${r.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-slate-800">{r.name}</p>
                    <p className="font-mono text-xs text-slate-400">{r.staff_id}</p>
                  </div>
                  {conf != null ? (
                    <div className="grid place-items-center h-12 w-12 rounded-full border-4 border-amber-100">
                      <span className="text-xs font-bold text-amber-600">{Math.round(conf)}%</span>
                    </div>
                  ) : (
                    <span className="text-xs font-semibold text-slate-400">No AI</span>
                  )}
                </div>

                <div className="flex gap-3 mt-4">
                  <Photo path={r.selfie_path} label="Clock-in Selfie" />
                  <Photo path={masterOf(r.user_id)} label="Master Photo" />
                </div>

                {r.audit_result?.reason && (
                  <p className="mt-3 text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2 line-clamp-3">{r.audit_result.reason}</p>
                )}

                <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{r.site_name || "—"}</span>
                  <span>{fmt(r.ts)}</span>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-2">
                  <button onClick={() => decide.mutate({ id: r.id, status: "present" })} disabled={busyId === r.id} data-testid="ai-review-approve-btn"
                    className="inline-flex items-center justify-center gap-1.5 h-10 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition disabled:opacity-60">
                    {busyId === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Approve
                  </button>
                  <button onClick={() => decide.mutate({ id: r.id, status: "absent" })} disabled={busyId === r.id} data-testid="ai-review-reject-btn"
                    className="inline-flex items-center justify-center gap-1.5 h-10 rounded-lg border border-rose-200 bg-rose-50 text-rose-600 text-sm font-semibold hover:bg-rose-100 transition disabled:opacity-60">
                    <X className="h-4 w-4" /> Reject
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
